import { EPaymentPlan } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import {
  Caption,
  RadioTable,
  useFieldPrerequisites,
  type FieldPrerequisiteSpec,
  type FieldPrerequisiteSpecShape,
} from '@if/mui'
import { Box, Stack, Tooltip } from '@mui/material'
import { formatComputed } from '@src/data/worksheet-selectors'
import { TermAndPlanOption } from '@src/types/TermPlanOptionDto'
import { EditPersonalLoanWorksheetDto } from '@src/types/WorksheetSchema'
import { useCallback } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useTermAndPlanColumns, useTermAndPlanDisabledReason } from './termAndPlanSelect-hooks'

const MERGED_TERM_PREREQUISITES = [
  { name: 'paymentFrequency', prefixKey: 'worksheet' },
  { name: 'deliveryOn', prefixKey: 'worksheet', labelKey: 'activationDate' },
  { name: 'firstPaymentOn', prefixKey: 'worksheet' },
  { name: 'amountRequested', prefixKey: 'worksheet', labelKey: 'requestedAmount' },
] as const satisfies readonly FieldPrerequisiteSpec<EditPersonalLoanWorksheetDto, 'worksheet'>[]

interface Props {
  options: TermAndPlanOption[]
  isLoading: boolean
  maxPmtAmount: number
  prerequisites?: readonly FieldPrerequisiteSpecShape<EditPersonalLoanWorksheetDto>[]
}

/**
 * The worksheet's term & plan field: binds the `RadioTable` to `paymentPlanId` +
 * `term`, supplies the columns and the max-payment rule, and shows the field error.
 * It brings no surface of its own — the consumer decides whether it sits in a card
 * and what else goes with it.
 */
const TermAndPlanSelect = ({ options, isLoading, maxPmtAmount, prerequisites = MERGED_TERM_PREREQUISITES }: Props) => {
  const { t } = useTranslation()
  const { control } = useFormContext<EditPersonalLoanWorksheetDto>()
  const columns = useTermAndPlanColumns()
  const getRowDisabled = useTermAndPlanDisabledReason()
  const termPrerequisites = useFieldPrerequisites(control, prerequisites, 'term')

  const { field: planField } = useController({ control, name: 'paymentPlanId' })
  const { field: termField, fieldState: termFieldState } = useController({ control, name: 'term' })

  const currentKey = termField.value != null ? `${planField.value}|${termField.value}` : null
  const selectedOption = options.find((o) => o.key === currentKey)

  const handleChange = useCallback(
    (_key: string, option: TermAndPlanOption) => {
      planField.onChange(option.paymentPlanId as EPaymentPlan)
      termField.onChange(option.termMonths)
    },
    [planField, termField],
  )

  const getSummary = useCallback(
    (option: TermAndPlanOption) =>
      [
        t('worksheet.termMonths', { count: option.termMonths }),
        option.planLabel ?? t('worksheet.regularPlan'),
        formatComputed(option.monthlyPayment, formatCurrency),
      ].join('  ·  '),
    [t],
  )

  const table = (
    <RadioTable
      title={t('worksheet.termAndPlan')}
      getSummary={getSummary}
      rows={options}
      columns={columns}
      getRowId={(option) => option.key}
      value={selectedOption ? currentKey : null}
      onChange={handleChange}
      getRowDisabled={getRowDisabled}
      tooltip={t('worksheet.maxPaymentRule', { amount: formatCurrency(maxPmtAmount) })}
      // Skeletons only before there is anything to show. A later refetch (the
      // protection plan being toggled) keeps the current rows on screen.
      loading={isLoading && options.length === 0}
      disabled={!termPrerequisites.isReady}
    />
  )

  return (
    <Stack spacing={1}>
      {termPrerequisites.isReady ? (
        table
      ) : (
        <Tooltip title={termPrerequisites.tooltip} placement="top">
          <Box>{table}</Box>
        </Tooltip>
      )}
      {termPrerequisites.isReady && termFieldState.error?.message && (
        <Caption sx={{ color: 'error.main' }}>{termFieldState.error.message}</Caption>
      )}
    </Stack>
  )
}

export default TermAndPlanSelect
